import { useEffect, useState } from 'react'
import api from '../services/api'

// Histórico de cargos do funcionário (WCMOTCAC/TMOTCAF do ERP, frota.motcaf). Chave = data inicial.

interface Registro { ini: string; fim: string; car: string; cardes: string; usu: string }
interface Cargo { catcod: string; catdes: string }
interface Form { iniAnterior: string; ini: string; fim: string; car: string }

const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const VAZIO: Form = { iniAnterior: '', ini: '', fim: '', car: '' }

export default function FuncionarioCargosModal({ motcod, motnome, onClose, onCargoAtual }: {
  motcod: string; motnome: string; onClose: () => void; onCargoAtual: (cargo: string) => void
}) {
  const [lista, setLista] = useState<Registro[] | null>(null)
  const [cargos, setCargos] = useState<Cargo[]>([])
  const [f, setF] = useState<Form>(VAZIO)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  const base = `/funcionarios/${encodeURIComponent(motcod)}/cargos`
  const deLinha = (x: Registro): Form => ({ iniAnterior: x.ini, ini: x.ini, fim: x.fim, car: x.car })

  async function carregar(manterIni?: string) {
    const r = await api.get(base)
    setLista(r.data)
    const alvo = r.data.find((x: Registro) => x.ini === manterIni)
    setF(alvo ? deLinha(alvo) : VAZIO)
  }

  useEffect(() => {
    api.get('/categorias-profissionais').then(r => setCargos(r.data)).catch(() => {})
    carregar().catch(() => setErro('Não foi possível carregar o histórico de cargos.'))
  }, [motcod])

  async function salvar() {
    setErro(''); setAviso('')
    if (!f.ini) return setErro('Informe a data inicial.')
    if (!f.car) return setErro('Informe o cargo.')
    setSalvando(true)
    try {
      const r = await api.put(base, f)
      await carregar(r.data.ini)
      if (r.data.motstatus) onCargoAtual(r.data.motstatus)
      setAviso('Cargo salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o cargo.')
    } finally {
      setSalvando(false)
    }
  }

  async function excluir() {
    if (!f.iniAnterior || !window.confirm(`Excluir o cargo iniciado em ${fmtData(f.iniAnterior)}?`)) return
    setErro(''); setAviso('')
    try {
      const r = await api.delete(`${base}/${f.iniAnterior}`)
      await carregar()
      if (r.data.motstatus) onCargoAtual(r.data.motstatus)
      setAviso('Cargo excluído.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao excluir o cargo.')
    }
  }

  const set = (c: keyof Form, v: string) => setF(p => ({ ...p, [c]: v }))
  const th: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.35rem 0.6rem', fontSize: '0.82rem' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Histórico de Cargos — {motcod} · {motnome}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCAF</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!lista ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <table className="table" style={{ width: '100%', margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Data Inicial</th>
                      <th style={th}>Data Final</th>
                      <th style={{ ...th, width: 50 }}>Código</th>
                      <th style={th}>Descrição do Cargo</th>
                      <th style={th}>Usuário</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.length === 0 && (
                      <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhum cargo no histórico.</td></tr>
                    )}
                    {lista.map(x => {
                      const ativo = x.ini === f.iniAnterior
                      return (
                        <tr key={x.ini} onClick={() => { setF(deLinha(x)); setErro(''); setAviso('') }}
                          style={{ cursor: 'pointer', background: ativo ? '#eef3fb' : undefined, fontWeight: ativo ? 600 : undefined }}>
                          <td style={td}>{fmtData(x.ini)}</td>
                          <td style={td}>{fmtData(x.fim) || 'Em aberto'}</td>
                          <td style={td}>{x.car}</td>
                          <td style={td}>{x.cardes}</td>
                          <td style={td}>{x.usu}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 0.6rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {f.iniAnterior ? `Cargo de ${fmtData(f.iniAnterior)}` : 'Novo cargo'}
                </span>
                {!!f.iniAnterior && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setF(VAZIO); setErro(''); setAviso('') }}>+ Novo cargo</button>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }}>
                <div className="field-cad" style={{ flex: '0 0 140px' }}>
                  <label>Data Inicial *</label>
                  <input type="date" value={f.ini} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('ini', e.target.value)} />
                </div>
                <div className="field-cad" style={{ flex: '0 0 140px' }}>
                  <label>Data Final</label>
                  <input type="date" value={f.fim} min={f.ini || undefined} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('fim', e.target.value)} />
                </div>
                <div className="field-cad" style={{ flex: '1 1 280px' }}>
                  <label>Cargo *</label>
                  <select value={f.car} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('car', e.target.value)}>
                    <option value="">Selecione...</option>
                    {cargos.map(c => <option key={c.catcod} value={c.catcod}>{c.catcod} - {c.catdes}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {lista && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          {!!f.iniAnterior && <button className="btn btn-secondary" style={{ marginRight: 'auto', color: '#dc2626' }} onClick={excluir}>Excluir</button>}
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !lista}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
